import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import DatePicker from 'react-datepicker'

import { formatDate, filterDates, parseLocalDateInput } from 'helpers/helpers'
import { businessDayStartOf, businessDayEndOf } from 'helpers/businessDay'

const DEFAULT_PRESETS = ['today', 'yesterday', 'currentWeek', 'lastWeek', 'currentMonth', 'lastMonth']

const PRESET_KEYS = {
	'today': 'today',
	'yesterday': 'yesterday',
	'currentWeek': 'current_week',
	'lastWeek': 'last_week',
	'currentMonth': 'current_month',
	'lastMonth': 'last_month',
}

const PRESET_ICONS = {
	'today': 'uil-calendar-alt',
	'yesterday': 'uil-history',
	'currentWeek': 'uil-schedule',
	'lastWeek': 'uil-history-alt',
	'currentMonth': 'uil-calender',
	'lastMonth': 'uil-redo',
}

const FORMAT_VALUE = 'yyyy-MM-dd HH:mm:ss'
const FORMAT_DATE = 'yyyy-MM-dd'

// Язык кабинета → локаль для названий месяцев
const INTL_LOCALES = { 'ru': 'ru', 'uz_latn': 'uz', 'uz_cyrl': 'uz-Cyrl', 'tr': 'tr' }

function monthNames(language) {
	const formatter = new Intl.DateTimeFormat(INTL_LOCALES[language] ?? 'ru', { 'month': 'long' })
	return Array.from({ length: 12 }, (_, month) => {
		const name = formatter.format(new Date(2000, month, 1))
		return name.charAt(0).toUpperCase() + name.slice(1)
	})
}

// Годы в списке: пять лет назад — следующий год
function yearOptions() {
	const current = new Date().getFullYear()
	return Array.from({ length: 7 }, (_, index) => current - 5 + index)
}

function startOfDay(date) {
	const result = new Date(date)
	result.setHours(0, 0, 0, 0)
	return result
}

// Время внутри операционного дня даты: при начале дня 03:00 время 01:00 — это уже
// следующие календарные сутки (хвост того же бизнес-дня)
function atBusinessTime(day, time) {
	const dayStart = formatDate(businessDayStartOf(day), 'HH:mm')
	const [hours, minutes] = String(time || dayStart).split(':').map(Number)
	const result = startOfDay(day)
	result.setHours(hours, minutes, 0, 0)
	if (String(time) < dayStart) result.setDate(result.getDate() + 1)
	return result
}

// Календарный день, к которому относится момент по бизнес-дню
function businessDayOf(value) {
	if (!value) return null
	const dayStart = formatDate(businessDayStartOf(value), 'HH:mm')
	const day = startOfDay(value)
	if (formatDate(value, 'HH:mm') < dayStart) day.setDate(day.getDate() - 1)
	return day
}

/**
 * Выбор периода одним окном: слева готовые периоды, справа два месяца — первый клик
 * начало, второй конец, снизу время. Границы по умолчанию — операционный день точки.
 *
 * Окно — отдельный слой по центру экрана: поле часто стоит внизу панели «Фильтр»,
 * и выпадающий вниз календарь уходил за край. Клики внутри слоя не доходят до документа,
 * поэтому выпадающая панель фильтра под ним не закрывается.
 *
 * startDate / endDate — строки 'yyyy-MM-dd HH:mm:ss' (withTime) или 'yyyy-MM-dd' (withTime={false}).
 * onChange({ start_date, end_date }, presetId) — в том же формате; presetId — id готового
 * периода ('today', 'lastMonth'…) или null, если период выбран в календаре.
 * presets={[]} — без колонки готовых периодов (у экрана свои кнопки периода).
 * minDate / maxDate — Date: границы, за которые календарь не пускает.
 * clearable — необязательный период: крестик в поле отдаёт onChange({ start_date: '', end_date: '' }, null).
 */
function PeriodPicker({ startDate, endDate, onChange, presets = DEFAULT_PRESETS, withTime = true, minDate = null, maxDate = null, clearable = false }) {
	const { t, i18n } = useTranslation()
	const months = useMemo(() => monthNames(i18n.language), [i18n.language])

	const [open, setOpen] = useState(false)
	const [draft, setDraft] = useState({ 'start': null, 'end': null, 'startTime': '', 'endTime': '' })

	const start = parseLocalDateInput(startDate)
	const end = parseLocalDateInput(endDate)

	// С временем — границы операционного дня; только даты — календарные дни как есть
	function dayOf(value) {
		if (!value) return null
		return withTime ? businessDayOf(value) : startOfDay(value)
	}

	function presetRange(presetId) {
		const range = filterDates(presetId)
		const from = parseLocalDateInput(range.start_date)
		const to = parseLocalDateInput(range.end_date)
		if (!from || !to) return null
		return withTime
			? { 'start_date': formatDate(businessDayStartOf(from), FORMAT_VALUE), 'end_date': formatDate(businessDayEndOf(to), FORMAT_VALUE) }
			: { 'start_date': formatDate(from, FORMAT_DATE), 'end_date': formatDate(to, FORMAT_DATE) }
	}

	// Свой контейнер слоя: на нём глушим всплытие click, чтобы Dropdown фильтра
	// не принял клик в календаре за клик снаружи. Слушатель React на этом же
	// контейнере срабатывает — до документа событие просто не доходит
	const layer = useMemo(() => {
		const element = document.createElement('div')
		element.className = 'period-picker-layer'
		element.addEventListener('click', (e) => e.stopPropagation())
		return element
	}, [])

	useEffect(() => {
		if (!open) return
		document.body.appendChild(layer)
		function handleKey(e) {
			if (e.key === 'Escape') setOpen(false)
		}
		document.addEventListener('keydown', handleKey)
		return () => {
			document.removeEventListener('keydown', handleKey)
			if (layer.parentNode) layer.parentNode.removeChild(layer)
		}
	}, [open, layer])

	function openPicker() {
		setDraft({
			'start': dayOf(start),
			'end': dayOf(end),
			'startTime': start ? formatDate(start, 'HH:mm') : formatDate(businessDayStartOf(new Date()), 'HH:mm'),
			'endTime': end ? formatDate(end, 'HH:mm') : formatDate(businessDayEndOf(new Date()), 'HH:mm'),
		})
		setOpen(true)
	}

	function selectPreset(presetId) {
		const range = presetRange(presetId)
		if (!range) return
		onChange(range, presetId)
		setOpen(false)
	}

	function selectRange([from, to]) {
		setDraft({ ...draft, 'start': from, 'end': to })
	}

	function apply() {
		if (!draft.start || !draft.end) return
		if (!withTime) {
			onChange({ 'start_date': formatDate(draft.start, FORMAT_DATE), 'end_date': formatDate(draft.end, FORMAT_DATE) }, null)
			setOpen(false)
			return
		}
		const from = atBusinessTime(draft.start, draft.startTime)
		const to = atBusinessTime(draft.end, draft.endTime)
		// Конец выбран по минутам — включаем всю последнюю минуту
		to.setSeconds(59)
		onChange({ 'start_date': formatDate(from, FORMAT_VALUE), 'end_date': formatDate(to, FORMAT_VALUE) }, null)
		setOpen(false)
	}

	// Какой готовый период совпадает с выбранным — его подсвечиваем
	function activePreset() {
		if (!start || !end) return null
		const format = withTime ? FORMAT_VALUE : FORMAT_DATE
		return presets.find(presetId => {
			const range = presetRange(presetId)
			return range &&
				range.start_date === formatDate(start, format) &&
				range.end_date === formatDate(end, format)
		}) ?? null
	}

	// Шапка месяца: у левого — стрелка назад и списки месяца и года (год меняется сразу,
	// без листания по месяцу), у правого — название и стрелка вперёд
	function renderMonthHeader({ monthDate, customHeaderCount, changeMonth, changeYear, decreaseMonth, increaseMonth }) {
		if (customHeaderCount > 0) {
			return (
				<div className="period-picker-month-header">
					<span className="period-picker-month-name">
						{months[monthDate.getMonth()]} {monthDate.getFullYear()}
					</span>
					<button type="button" className="period-picker-nav" onClick={increaseMonth}>
						<i className="uil uil-angle-right"></i>
					</button>
				</div>
			)
		}

		const years = yearOptions()
		if (!years.includes(monthDate.getFullYear())) years.unshift(monthDate.getFullYear())

		return (
			<div className="period-picker-month-header">
				<button type="button" className="period-picker-nav" onClick={decreaseMonth}>
					<i className="uil uil-angle-left"></i>
				</button>
				<select className="form-select period-picker-select"
					value={monthDate.getMonth()}
					onChange={(e) => changeMonth(Number(e.target.value))}>
					{months.map((name, month) => (
						<option key={month} value={month}>{name}</option>
					))}
				</select>
				<select className="form-select period-picker-select is-year"
					value={monthDate.getFullYear()}
					onChange={(e) => changeYear(Number(e.target.value))}>
					{years.map(year => (
						<option key={year} value={year}>{year}</option>
					))}
				</select>
			</div>
		)
	}

	const fieldFormat = withTime ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy'
	const presetId = activePreset()
	const draftFormat = 'dd.MM.yyyy'

	return (
		<div className="period-picker">
			<button type="button" className={`period-picker-field form-control${open ? ' is-open' : ''}`}
				onClick={() => openPicker()}>
				<i className="uil uil-calendar-alt"></i>
				{(start || end)
					? <span className="period-picker-value">
						{start ? formatDate(start, fieldFormat) : '…'}
						<span className="period-picker-dash">—</span>
						{end ? formatDate(end, fieldFormat) : '…'}
					</span>
					: <span className="period-picker-value is-empty">{t('period_not_selected')}</span>
				}
				{presetId &&
					<span className="period-picker-tag">{t(PRESET_KEYS[presetId] ?? presetId)}</span>
				}
				{clearable && (start || end) &&
					<span className="period-picker-clear" role="button" title={t('clear')}
						onClick={(e) => {
							e.stopPropagation()
							onChange({ 'start_date': '', 'end_date': '' }, null)
						}}>
						<i className="uil uil-times"></i>
					</span>
				}
			</button>

			{open && createPortal(
				<div className="period-picker-backdrop" onMouseDown={(e) => {
					if (e.target === e.currentTarget) setOpen(false)
				}}>
					<div className="period-picker-dialog" role="dialog">
						<div className="period-picker-header">
							<div className="period-picker-title">
								<i className="uil uil-calendar-alt"></i>
								<span>{t('period')}</span>
							</div>
							<div className="period-picker-range">
								{draft.start ? formatDate(draft.start, draftFormat) : '—'}
								<span className="period-picker-dash">—</span>
								{draft.end ? formatDate(draft.end, draftFormat) : '—'}
							</div>
							<button type="button" className="period-picker-close" onClick={() => setOpen(false)}>
								<i className="uil uil-times"></i>
							</button>
						</div>

						<div className="period-picker-body">
							{presets.length > 0 &&
							<div className="period-picker-presets">
								{presets.map(id => (
									<button key={id} type="button"
										className={`period-picker-preset${presetId === id ? ' active' : ''}`}
										onClick={() => selectPreset(id)}>
										<i className={`uil ${PRESET_ICONS[id] ?? 'uil-calendar-alt'}`}></i>
										<span>{t(PRESET_KEYS[id] ?? id)}</span>
									</button>
								))}
							</div>
							}

							<div className="period-picker-calendar">
								<DatePicker
									inline
									selectsRange
									monthsShown={2}
									startDate={draft.start}
									endDate={draft.end}
									selected={draft.start}
									openToDate={draft.start ?? new Date()}
									renderCustomHeader={renderMonthHeader}
									minDate={minDate}
									maxDate={maxDate}
									onChange={selectRange}
								/>
							</div>
						</div>

						<div className="period-picker-footer">
							{withTime &&
								<div className="period-picker-times">
									<label className="period-picker-time">
										<span>{t('start')}</span>
										<input type="time" className="form-control"
											value={draft.startTime}
											onChange={(e) => setDraft({ ...draft, 'startTime': e.target.value })} />
									</label>
									<label className="period-picker-time">
										<span>{t('end')}</span>
										<input type="time" className="form-control"
											value={draft.endTime}
											onChange={(e) => setDraft({ ...draft, 'endTime': e.target.value })} />
									</label>
								</div>
							}
							<div className="period-picker-hint">
								{!draft.start || !draft.end ? t('period_pick_hint') : ''}
							</div>
							<div className="period-picker-actions">
								<button type="button" className="btn btn-secondary" onClick={() => setOpen(false)}>
									{t('cancel')}
								</button>
								<button type="button" className="btn btn-primary"
									disabled={!draft.start || !draft.end}
									onClick={() => apply()}>
									{t('apply')}
								</button>
							</div>
						</div>
					</div>
				</div>,
				layer
			)}
		</div>
	)
}

export default PeriodPicker
